function normalizeWhitespace(value) {
    return String(value || '')
        .replace(/\r\n?/g, '\n')
        .replaceAll(String.fromCharCode(0), '')
        .replace(/[ \t]+\n/g, '\n')
        .replace(/\n{4,}/g, '\n\n\n')
        .trim();
}

function stripTrailingColon(value) {
    return String(value || '')
        .replace(/:\s*$/, '')
        .trim();
}

function getHeadingLevel(sectionNumber) {
    const depth = String(sectionNumber || '')
        .split('.')
        .filter(Boolean).length;

    if (depth <= 1) return '##';
    if (depth === 2) return '###';
    return '####';
}

function formatMetadataLine(value) {
    const match = String(value || '').match(
        /^([A-Za-z][A-Za-z0-9 '&()/.-]{1,80}):\s+(.+)$/
    );

    if (!match) return value;

    return `**${match[1].trim()}:** ${match[2].trim()}`;
}

function isLikelyStandaloneSubheading(value, nextLine = '') {
    const trimmed = String(value || '').trim();
    if (!trimmed || trimmed.length > 80) return false;
    if (/[:.!?]$/.test(trimmed)) return false;
    if (trimmed.includes('://')) return false;
    if (/^\d/.test(trimmed)) return false;

    const next = String(nextLine || '').trim();
    if (!next) return false;

    const words = trimmed.split(/\s+/).filter(Boolean);
    if (words.length < 2 || words.length > 10) return false;

    const connectorWords = new Set([
        'a',
        'an',
        'and',
        'as',
        'at',
        'by',
        'for',
        'in',
        'of',
        'on',
        'or',
        'the',
        'to',
        'with',
    ]);

    const matchingWords = words.filter((word) => {
        const normalized = word.replace(/^[("']+|[)"',.]+$/g, '');
        if (!normalized) return false;
        if (connectorWords.has(normalized.toLowerCase())) return true;
        return /^[A-Z0-9]/.test(normalized);
    });

    return matchingWords.length / words.length >= 0.7;
}

export function normalizeLegalDocumentText(value) {
    return normalizeWhitespace(value)
        .replace(/\u00a0/g, ' ')
        .replace(/[ \t]{2,}/g, ' ')
        .trim();
}

export function splitLegalDocumentTitleAndBody(documentText, fallbackTitle) {
    const normalizedText = normalizeLegalDocumentText(documentText);
    if (!normalizedText) {
        return {
            title: fallbackTitle,
            body: '',
        };
    }

    const lines = normalizedText.split('\n');
    const titleIndex = lines.findIndex((line) => line.trim());
    const title =
        titleIndex >= 0 ? lines[titleIndex].trim() : String(fallbackTitle);
    const body = lines.slice(titleIndex >= 0 ? titleIndex + 1 : 0).join('\n');

    return {
        title: title || fallbackTitle,
        body: body.trim(),
    };
}

export function formatLegalDocumentMarkdown(documentText) {
    const normalizedText = normalizeLegalDocumentText(documentText);
    if (!normalizedText) return '';

    const lines = normalizedText.split('\n');
    const blocks = [];
    let paragraphLines = [];
    let listItems = [];

    const flushParagraph = () => {
        if (!paragraphLines.length) return;
        blocks.push(paragraphLines.join(' ').replace(/\s+/g, ' ').trim());
        paragraphLines = [];
    };

    const flushList = () => {
        if (!listItems.length) return;
        blocks.push(listItems.join('\n'));
        listItems = [];
    };

    for (let index = 0; index < lines.length; index += 1) {
        const currentLine = lines[index];
        const trimmed = currentLine.trim();
        const nextLine = lines
            .slice(index + 1)
            .map((line) => line.trim())
            .find(Boolean);

        if (!trimmed) {
            flushParagraph();
            flushList();
            continue;
        }

        const bulletMatch = trimmed.match(/^[•◦▪●*-]\s+(.+)$/);
        if (bulletMatch) {
            flushParagraph();
            listItems.push(`- ${formatMetadataLine(bulletMatch[1].trim())}`);
            continue;
        }

        const numberedHeadingMatch = trimmed.match(
            /^(\d+(?:\.\d+){0,3})(?:\.)?\s+(.+)$/
        );
        if (numberedHeadingMatch) {
            flushParagraph();
            flushList();
            const [, sectionNumber, headingText] = numberedHeadingMatch;
            blocks.push(
                `${getHeadingLevel(
                    sectionNumber
                )} ${sectionNumber} ${stripTrailingColon(headingText)}`
            );
            continue;
        }

        const metadataMatch = trimmed.match(
            /^([A-Za-z][A-Za-z0-9 '&()/.-]{1,80}):\s+(.+)$/
        );
        if (metadataMatch) {
            flushParagraph();
            flushList();
            blocks.push(formatMetadataLine(trimmed));
            continue;
        }

        if (
            /^[A-Z][^.!?]{1,120}:$/.test(trimmed) ||
            isLikelyStandaloneSubheading(trimmed, nextLine)
        ) {
            flushParagraph();
            flushList();
            blocks.push(`#### ${stripTrailingColon(trimmed)}`);
            continue;
        }

        flushList();
        paragraphLines.push(trimmed);
    }

    flushParagraph();
    flushList();

    return blocks.join('\n\n').trim();
}

export function getLegalDocumentFields(
    documentText,
    { fallbackTitle = 'Legal Information', tagline = 'Legal Information' } = {}
) {
    const { title, body } = splitLegalDocumentTitleAndBody(
        documentText,
        fallbackTitle
    );

    return {
        Tagline: tagline,
        Header: title,
        DocumentContent: formatLegalDocumentMarkdown(body || documentText),
    };
}
